import { history, request } from '@umijs/max';
import { showApiErrorMessage } from '@/utils/apiMessage';

export const LOGIN_PATH = '/user/login';
export const SUB_LOGIN_PATH = '/user/sub-login';
export const SESSION_EXPIRED_FLAG = 'geo_session_expired';

export const isLoginPath = (pathname: string) =>
  pathname === LOGIN_PATH || pathname.endsWith('/user/login');

export const isSubLoginPath = (pathname: string) =>
  pathname === SUB_LOGIN_PATH || pathname.endsWith('/user/sub-login');

export const isSharePath = (pathname: string) => pathname.startsWith('/share');

export const isAssetFillPath = (pathname: string) =>
  pathname === '/asset-fill' || pathname.startsWith('/asset-fill/');

/** 无需登录态校验的页面 */
export const isAuthExemptPath = (pathname: string) =>
  isLoginPath(pathname) || isSubLoginPath(pathname) || isSharePath(pathname) || isAssetFillPath(pathname);

export const getAuthToken = (): string => {
  if (typeof window === 'undefined') return '';
  return (
    sessionStorage.getItem('token') ||
    localStorage.getItem('token') ||
    ''
  ).trim();
};

export const hasAuthToken = (): boolean => getAuthToken() !== '';

export const readStoredUserInfo = <T = Record<string, unknown>>(): T | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw =
      sessionStorage.getItem('userInfo') ||
      localStorage.getItem('userInfo');
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

const getAuthStorageScope = (): 'session' | 'local' => {
  if (typeof window !== 'undefined' && sessionStorage.getItem('token')) {
    return 'session';
  }
  return 'local';
};

/** 与登录写入规则一致：代登录写 sessionStorage，普通登录写 localStorage */
export const persistUserInfo = (userInfo: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  const json = JSON.stringify(userInfo);
  if (getAuthStorageScope() === 'session') {
    sessionStorage.setItem('userInfo', json);
  } else {
    localStorage.setItem('userInfo', json);
  }
};

/** 清除全部登录缓存（token + userInfo） */
export const clearAuthSession = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('userInfo');
};

export const markSessionExpired = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1');
};

export const consumeSessionExpiredNotice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const flagged = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
  if (flagged) {
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
  }
  return flagged;
};

/** 登录失效：清缓存、标记、提示并跳转登录页 */
export const handleUnauthorized = (
  message = '登录已过期，请重新登录',
  options?: { silent?: boolean; redirect?: boolean },
) => {
  if (typeof window === 'undefined') return;

  const pathname = window.location.pathname;
  if (isAuthExemptPath(pathname)) {
    clearAuthSession();
    return;
  }

  clearAuthSession();
  markSessionExpired();

  if (!options?.silent) {
    showApiErrorMessage(-1, message);
  }

  if (options?.redirect !== false && !isLoginPath(pathname)) {
    history.push(LOGIN_PATH);
  }
};

/** 服务端校验当前 Token（不触发全局 errorHandler，避免重复弹窗） */
export const fetchUserProfileSafe = async () => {
  return request<GEO.ApiResponse<GEO.UserInfo>>('/api/user/profile', {
    method: 'GET',
    skipErrorHandler: true,
  });
};

/**
 * 启动时校验登录态：有 token 则拉 profile；无效则清缓存
 * @returns 有效用户信息，否则 undefined
 */
export const validateAuthSession = async (): Promise<API.CurrentUser | undefined> => {
  if (typeof window === 'undefined') {
    return readStoredUserInfo<API.CurrentUser>();
  }

  const pathname = window.location.pathname;
  // 代登录页：勿读取父标签页 localStorage 里的运营/代理账号，避免余额隐藏等判断串号
  if (isSubLoginPath(pathname)) {
    return undefined;
  }
  if (isAuthExemptPath(pathname)) {
    return readStoredUserInfo<API.CurrentUser>();
  }

  if (!hasAuthToken()) {
    if (readStoredUserInfo()) {
      clearAuthSession();
      if (!isAuthExemptPath(pathname)) {
        markSessionExpired();
        history.replace(LOGIN_PATH);
      }
    }
    return undefined;
  }

  try {
    const res = await fetchUserProfileSafe();
    if (res?.code === 1 && res.data) {
      const userInfo = res.data as API.CurrentUser;
      persistUserInfo(userInfo as Record<string, unknown>);
      return userInfo;
    }

    handleUnauthorized(res?.msg || '登录已过期，请重新登录', { silent: true });
    return undefined;
  } catch {
    handleUnauthorized('登录已过期，请重新登录', { silent: true });
    return undefined;
  }
};

export const isAuthErrorCode = (code?: number) => code === -1 || code === 401;
